import * as Sentry from "@sentry/react";
//#region types
import { AgentTurn, isShownAnswer, outcomeOf } from "models/agent/agentContract";
//#endregion
import EnvHelper from "./envHelper";

// Usage tracking of the support agent popper, as Sentry Logs. Events carry codes, ids and counts
// only - never what the customer wrote or what the agent showed.

export const AgentTrackingFeature = "support-agent";

export type AgentTrackingValue = string | number | boolean;

/** Who an event is about: the conversation, the agent's server session and the company. */
export interface AgentTrackingContext {
  conversationId: string;
  sessionId: string | null;
  companyId: number;
}

export const trackAgent = (
  context: AgentTrackingContext,
  event: string,
  attributes: Record<string, AgentTrackingValue | undefined> = {}
) => {
  const defined = Object.fromEntries(Object.entries(attributes).filter(([, value]) => value !== undefined));
  Sentry.logger.info(`support_agent.${event}`, {
    feature: AgentTrackingFeature,
    event,
    conversationId: context.conversationId,
    sessionId: context.sessionId ?? "",
    companyId: context.companyId,
    clientVersion: EnvHelper.getVersion(),
    ...defined,
  });
};

export type AgentEnding =
  | "support_sent"
  | "review_abandoned"
  | "answered"
  | "question_abandoned"
  | "agent_unavailable"
  | "error"
  | "no_message";

/** How a conversation ended, from its turns; the first rule that matches wins. */
export const conversationEnding = (
  turns: AgentTurn[],
  agentUnavailable: boolean,
  sentToSupport: boolean,
  reviewShown: boolean
): AgentEnding => {
  if (sentToSupport) return "support_sent";
  if (reviewShown) return "review_abandoned";

  const lastAgentTurn = turns.filter((turn) => turn.who === "agent").at(-1);
  const lastReply = lastAgentTurn?.reply;
  const lastOutcome = lastReply?.kind === "answer" ? outcomeOf(lastReply) : undefined;
  const lastShownAnswer = lastReply?.kind === "answer" && isShownAnswer(lastReply);
  if (lastReply?.kind === "card" || lastReply?.kind === "receipt" || lastShownAnswer) return "answered";
  if (lastReply?.kind === "question" || lastOutcome === "NeedsClarification") return "question_abandoned";
  if (agentUnavailable) return "agent_unavailable";
  // A failure shown, or her question left with no answer (a reload mid-request).
  if (lastAgentTurn?.failed || turns.some((turn) => turn.who === "customer")) return "error";
  return "no_message";
};
